import { KeyboardEvent, useEffect, useRef, useState } from "react";

/**
 * Deleting a line is now a THREE-step action everywhere in the app: the
 * trash icon opens this dialog, which first asks for the shared delete
 * password before it will even show the usual "are you sure?" step. Wrong
 * password closes the whole thing (Access denied) rather than looping back —
 * a second guess is one more click away regardless.
 *
 * Deliberately no <form> anywhere in here: every tab that can show this
 * dialog already wraps its own content in a <form> (for the camera-scan
 * fields), and a <form> nested inside another is invalid HTML — the browser
 * can end up submitting the OUTER form for real, reloading the whole app
 * and dropping the operator back on the first tab. Enter-to-submit is wired
 * by hand instead, on the input's own keydown.
 */

const DELETE_PASSWORD = "skynet";

type Step = "password" | "denied" | "confirm";

export default function ConfirmDialog({
  title,
  message,
  confirmLabel = "OK",
  busyLabel = "WORKING…",
  isBusy = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  confirmLabel?: string;
  busyLabel?: string;
  isBusy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [step, setStep] = useState<Step>("password");
  const [password, setPassword] = useState("");
  const passwordRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const deniedRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (step === "password") passwordRef.current?.focus();
    // The Cancel button gets focus for the "are you sure?" step, not the
    // destructive one — so a leftover Enter keypress from typing the
    // password can never itself confirm a delete.
    if (step === "confirm") cancelRef.current?.focus();
    if (step === "denied") deniedRef.current?.focus();
  }, [step]);

  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onCancel]);

  const submitPassword = () => {
    setStep(password === DELETE_PASSWORD ? "confirm" : "denied");
  };

  const onPasswordKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitPassword();
    }
  };

  return (
    <div
      className="confirm-backdrop"
      role="presentation"
      onClick={() => !isBusy && onCancel()}
    >
      <div
        className="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-message"
        onClick={(event) => event.stopPropagation()}
      >
        {step === "password" && (
          <>
            <h3 id="confirm-title">Password required</h3>
            <p id="confirm-message">Enter the password to continue.</p>
            <input
              ref={passwordRef}
              className="confirm-password-input"
              type="password"
              autoComplete="off"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={onPasswordKeyDown}
            />
            <div className="confirm-actions">
              <button
                type="button"
                className="confirm-cancel"
                onClick={onCancel}
              >
                CANCEL
              </button>
              <button
                type="button"
                className="confirm-ok"
                onClick={submitPassword}
              >
                CONTINUE
              </button>
            </div>
          </>
        )}

        {step === "denied" && (
          <>
            <h3 id="confirm-title">Access denied</h3>
            <p id="confirm-message">The password was not correct.</p>
            <div className="confirm-actions">
              <button
                ref={deniedRef}
                className="confirm-ok"
                type="button"
                onClick={onCancel}
              >
                CLOSE
              </button>
            </div>
          </>
        )}

        {step === "confirm" && (
          <>
            <h3 id="confirm-title">{title}</h3>
            <p id="confirm-message">{message}</p>
            <div className="confirm-actions">
              <button
                ref={cancelRef}
                className="confirm-cancel"
                type="button"
                disabled={isBusy}
                onClick={onCancel}
              >
                CANCEL
              </button>
              <button
                className="confirm-ok"
                type="button"
                disabled={isBusy}
                onClick={onConfirm}
              >
                {isBusy ? busyLabel : confirmLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
